import { gql } from '@/apollo';

// Scalable Batch Actions (STMN-3358..3361).
// Replaces the per-trace init+client-sign+createLinks flow with a single
// server-side batch job. Poll progress with BATCH_JOB_QUERY.
export const CREATE_BATCH_ACTION_MUTATION = gql(`
  mutation createBatchActionMutation($input: CreateBatchActionInput!) {
    createBatchAction(input: $input) {
      jobId
    }
  }
`);

// Synchronous single-item retry (STMN-3361): re-runs one failed trace of an
// existing job in place (no new job/drawer) and returns its settled status.
export const RETRY_BATCH_ITEM_MUTATION = gql(`
  mutation retryBatchItemMutation($input: RetryBatchItemInput!) {
    retryBatchItem(input: $input) {
      jobId
      traceId
      status
    }
  }
`);

export const BATCH_JOB_QUERY = gql(`
  query batchJobQuery($id: String!) {
    batchJob(id: $id) {
      id
      status
      mode
      total
      processed
      succeeded
      failed
      createdAt
      startedAt
      completedAt
      actionKey
      actionTitle
      groupLabel
      runBy
      formData
      itemRunWindowMs
      itemsExpired
      failedItems {
        traceId
        error
      }
    }
  }
`);

// Reload re-attach (STMN batch progress enhancement): the caller's most-recent
// non-terminal job, or null. The overview fires this once on mount to re-open
// the progress drawer for a job still running after a page refresh; the regular
// BATCH_JOB_QUERY poll then takes over.
export const MY_RUNNING_BATCH_JOB_QUERY = gql(`
  query myRunningBatchJobQuery($workflowId: String!) {
    myRunningBatchJob(workflowId: $workflowId) {
      id
      status
    }
  }
`);

// Server-driven action list for filter-scan mode (STMN-3358 decision #3
// hardening): with 0 rows selected + an active filter, the batch tray asks
// the server which (groupLabel, actionKey) pairs at least ONE filter-matching
// trace can currently run. Cheap (existence check via GIN-backed
// NEXT_ACTIONS_PREDICATE), scales to 100k+ matching traces.
export const AVAILABLE_ACTIONS_FOR_FILTER_QUERY = gql(`
  query availableActionsForFilterQuery(
    $input: AvailableActionsForFilterInput!
  ) {
    availableActionsForFilter(input: $input) {
      pairs {
        groupLabel
        actionKey
      }
    }
  }
`);

// Pre-submit confirmation for filter-mode batch: exact count of traces
// matching the filter AND having (groupLabel, actionKey) available. Feeds the
// "will run on N of M matching, continue?" modal so a broad filter cannot
// silently narrow the batch impact without the user seeing it.
export const COUNT_MATCHING_FOR_ACTION_QUERY = gql(`
  query countMatchingForActionQuery(
    $input: CountMatchingForActionInput!
  ) {
    countMatchingForAction(input: $input) {
      count
    }
  }
`);

// Live completion feed: settled items of a batch job, keyset-paginated. The
// hook advances `after` with the newest cursor it has seen (constant per-poll
// cost); scroll-back uses `before`.
export const RECENT_BATCH_ITEMS_QUERY = gql(`
  query recentBatchItemsQuery($jobId: String!, $limit: Int, $after: String, $before: String) {
    recentBatchItems(jobId: $jobId, limit: $limit, after: $after, before: $before) {
      traceId
      traceName
      status
      updatedAt
      error
      attempts
      cursor
    }
  }
`);

export const WORKFLOW_BATCH_JOBS_QUERY = gql(`
  query workflowBatchJobsQuery($input: WorkflowBatchJobsInput!) {
    workflowBatchJobs(input: $input) {
      hasMore
      jobs {
        id
        status
        mode
        total
        processed
        succeeded
        failed
        createdAt
        startedAt
        completedAt
        actionKey
        actionTitle
        groupLabel
        runBy
        cursor
      }
    }
  }
`);

export const WORKFLOW_BATCH_JOB_SUBMITTERS_QUERY = gql(`
  query workflowBatchJobSubmittersQuery($workflowId: String!) {
    workflowBatchJobSubmitters(workflowId: $workflowId) {
      submitters {
        email
        name
        avatar
      }
    }
  }
`);
